import _ from 'lodash';
import lunr from 'lunr';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Search from '../Search';
import { layoutDispatch } from '../../services/LayoutService';
import { LIST_ITEM_DELIMITER } from '../../util/form';
import { lunrEscapeQuery } from '../../util/search';

class SimpleSearch extends Component {
  constructor(props) {
    super(props);
    this.state = {
      suggestions: [],
    };

    this.data = {};
  }

  componentDidMount() {
    const { list, value } = this.props;

    layoutDispatch(list.method).then(({ data }) => {
      this.createIndex(data.data);
      const suggestion = this.data[value];

      // Auto select suggestion
      if (suggestion) {
        this.props.onSuggestionSelected(this.getSuggestionValue(suggestion), suggestion);
      }
    });
  }

  componentDidUpdate(prevProps) {
    const { list } = this.props;

    // If the list config changes and it has a method tied to it
    // then it'll grab the new suggestions in the response of the method
    // TODO: This becomes a problem if this component is part of a large collection
    // because then it initiate a request for every index in the collection when the
    // response might be the same.
    if (!_.isEqual(list, prevProps.list)) {
      layoutDispatch(list.method).then(({ data }) => {
        this.createIndex(data.data);
      });
    }
  }

  onSuggestionSelected = (event, { suggestion }) => {
    if (suggestion === undefined) {
      return this.props.onSuggestionSelected('', '');
    }

    return this.props.onSuggestionSelected(this.getSuggestionValue(suggestion), suggestion);
  };

  onSuggestionsClearRequested = () => {
    this.setState({ suggestions: [] });
  };

  getSuggestionValue = (suggestion = {}) => {
    return `${suggestion.itemid}:${LIST_ITEM_DELIMITER}${suggestion.name}`;
  };

  createIndex(data = []) {
    if (!Array.isArray(data)) {
      return {};
    }

    this.index = lunr(function () {
      this.pipeline.remove(lunr.stopWordFilter);
      this.pipeline.remove(lunr.stemmer);
      this.searchPipeline.remove(lunr.stopWordFilter);
      this.searchPipeline.remove(lunr.stemmer);

      this.field('name');
      this.field('itemid');
      this.ref('itemid');

      data.forEach(entry => this.add(entry));
    });

    this.data = _.keyBy(data, 'itemid');
    return this.data;
  }

  handleSubmit = value => {
    if (!this.props.highlightFirstSuggestion) {
      this.props.onSuggestionSelected(value, value);
    }
  };

  shouldRenderSuggestions = value => {
    if (!value && this.state.suggestions) {
      return true;
    }

    return value.trim().length > 0;
  };

  loadSuggestions = value => {
    const { filter, limit } = this.props;

    if (value === '?' || value === '') {
      this.setState({ suggestions: filter(_.values(this.data).slice(0, 50)) });
      return;
    }

    const results = this.index ? this.index.search(`${lunrEscapeQuery(value)}*`).slice(0, limit) : [];

    const suggestions = [];
    const refMap = new Map();

    results.forEach(result => {
      refMap.set(result.ref, true);
      suggestions.push(this.data[result.ref]);
    });

    this.setState({ suggestions: filter(suggestions) });
  };

  renderSuggestion = (suggestion = {}) => {
    // Support for literal string suggestion
    if (suggestion && typeof suggestion !== 'object') {
      return <span>{suggestion}</span>;
    }

    if (suggestion.selectAll) {
      return <div style={{ fontWeight: 'bold' }}>Select All</div>;
    }

    // Grab the first key after itemid
    const name = _.without(Object.keys(suggestion), 'itemid')[0];
    if (suggestion[name] === undefined || suggestion[name] === '') {
      return <span>{suggestion.itemid}</span>;
    }

    let suggestionDetails = suggestion[name].split('\\n');
    const suggestionName = suggestionDetails.shift();
    suggestionDetails = suggestionDetails.map(line => {
      return <div className="suggestion-details">{line}</div>;
    });

    return (
      <span>
        {suggestion.itemid}: {suggestionName} {suggestionDetails}
      </span>
    );
  };

  render() {
    return (
      <Search
        {...this.props}
        debounce={0}
        getSuggestionValue={this.getSuggestionValue}
        loadSuggestions={this.loadSuggestions}
        onSubmit={this.handleSubmit}
        onSuggestionSelected={this.onSuggestionSelected}
        onSuggestionsClearRequested={this.onSuggestionsClearRequested}
        renderSuggestion={this.renderSuggestion}
        shouldRenderSuggestions={this.shouldRenderSuggestions}
        suggestions={this.state.suggestions}
      />
    );
  }
}

SimpleSearch.defaultProps = {
  filter: suggestions => suggestions,
  highlightFirstSuggestion: true,
  limit: 25,
  onBlur: v => v,
  onChange: v => v,
  showHidden: false,
  selectOnBlur: true,
  value: '',
};

SimpleSearch.propTypes = {
  controlId: PropTypes.string,
  filter: PropTypes.func,
  highlightFirstSuggestion: PropTypes.bool,
  limit: PropTypes.number,
  list: PropTypes.object,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onSuggestionSelected: PropTypes.func,
  selectOnBlur: PropTypes.bool,
  showHidden: PropTypes.bool,
  value: PropTypes.any,
};

export default SimpleSearch;
